/**
 * babyhyllan — hyllan. Behaviour for sections/bb-hylla.liquid.
 *
 * Two halves of one movement, on two pages:
 *
 * 1. Picking (the page with the shelf). A box is pulled off the shelf: it
 *    leaves an empty place, rises out of its row and grows into the middle
 *    of the screen while the wall colour fills in behind it. Then the
 *    browser follows the link. A note in sessionStorage tells the next page
 *    that this box was picked.
 *
 * 2. Opening (the collection page). The section has already put the same
 *    big box on screen, in the same place, before the page was painted (see
 *    the inline script in the section). Here it opens: the page scrolls the
 *    product grid into view behind it, the lid comes off, and the products
 *    jump out of the box one after another and fall into their own cards,
 *    where the real product takes over as each one lands. The wall colour
 *    fades away during the fall, so they land in the shop.
 *
 * Every step can be skipped: a tap, a key or a scroll finishes everything at
 * once. With reduced motion there is no movement at all; a picked box just
 * follows its link.
 *
 * On the home page what belongs together moves together: pointing at a
 * stage opens the ready boxes that fit it (data-boxes, decided in Liquid)
 * and makes the things in the room from that stage hop; pointing at a ready
 * box makes what is in it hop; pointing at a thing in the room opens the
 * boxes it comes in. On a phone, where nothing is pointed at, a tap chooses
 * instead and shows the same, with a card whose Öppna takes you on.
 *
 * 3. The present (the home page, first time in a visit; snippets/bb-rosett).
 *    The bow comes undone, the ribbons slip off, the paper opens to both
 *    sides, and the boxes, sitting in a heap in the middle of each plank,
 *    hop to their places one after the other, each lifting its lid as it
 *    lands. A tap opens the present at once; a tap, a key or a scroll while
 *    it opens finishes everything.
 */

const PICKED_KEY = 'bb-plock';

function stillness() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function wait(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/* ---------- 1. Picking a box off the shelf ---------- */

/** What can be chosen on the big shelf: the boxes and the things in the room. */
const CHOOSABLE = 'a[data-plock], a.bb-rum__sak';

class BbHylla extends HTMLElement {
  /** What a tap on a phone has chosen, waiting for Öppna. */
  chosen = null;
  /** What is lit up now: under the pointer, in focus or chosen. */
  shown = null;

  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    this.big = this.classList.contains('bb-hylla--stor');
    this.slots = [...this.querySelectorAll('.bb-hylla__slot')];
    this.things = [...this.querySelectorAll('.bb-rum__sak')];

    // In the slim row, bring the stage you are on into view.
    const here = this.querySelector('.bb-hylla__chip.is-har');
    const row = here?.closest('.bb-hylla__remsa');
    if (here instanceof HTMLElement && row instanceof HTMLElement && row.scrollWidth > row.clientWidth) {
      row.scrollLeft = here.offsetLeft - (row.clientWidth - here.offsetWidth) / 2;
    }

    // The last press, to tell a tap from a click with the mouse or a key.
    let press = null;
    this.addEventListener(
      'pointerdown',
      (event) => {
        const target = event.target instanceof Element ? event.target.closest(CHOOSABLE) : null;
        press = { touch: event.pointerType === 'touch', target, at: performance.now() };
      },
      { capture: true }
    );

    this.addEventListener('click', (event) => {
      const link = event.target instanceof Element ? event.target.closest(CHOOSABLE) : null;
      if (!link || !this.contains(link)) return;
      // A new tab or window is not a pick.
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      // On the big shelf a tap chooses first, so a phone sees what a pointer
      // would have shown; Öppna, or a second tap on the same, goes on. A
      // click with the mouse and a key (detail 0) go straight on.
      const tapped =
        !!press && press.touch && press.target === link && performance.now() - press.at < 1500 && event.detail > 0;
      if (this.big && tapped && this.chosen !== link) {
        event.preventDefault();
        this.#choose(link);
        return;
      }
      this.#go(link, event);
    });

    if (this.big) {
      for (const target of this.querySelectorAll(CHOOSABLE)) {
        target.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'touch') this.#show(target);
        });
        target.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'touch') this.#show(this.chosen);
        });
        target.addEventListener('focus', () => this.#show(target));
        target.addEventListener('blur', () => this.#show(this.chosen));
      }
      // Back with the browser's back button: nothing is chosen any more.
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.#choose(null);
      });
      // A turned phone moves the boxes, so the card follows.
      window.addEventListener('resize', () => {
        if (this.chosen) this.#card(this.chosen);
      });
    }
  }

  /** Follow a box (picked off the shelf) or a thing in the room (its page). */
  #go(link, event) {
    this.#choose(null);
    if (link.matches('a[data-plock]')) {
      event?.preventDefault();
      pick(link);
    } else if (!event) {
      window.location.assign(link.href);
    }
    // A thing in the room clicked: the browser follows the link itself.
  }

  /**
   * What belongs with `target` (a stage, a ready box or a thing in the room;
   * null for nothing) lights up. A stage opens the ready boxes that fit it
   * (data-boxes, decided in Liquid) and makes the things in the room from
   * that stage hop: the products of its collection, and for the step before
   * birth those of BB-väskan too (data-ocksa). A ready box makes what is in
   * it hop. A thing in the room opens the ready boxes it comes in. What does
   * not belong steps back meanwhile.
   */
  #show(target) {
    if (target === this.shown) return;
    this.shown = target;

    let boxes = null;
    let things = null;
    if (target?.matches('a[data-plock="steg"]')) {
      boxes = (target.dataset.boxes || '').split(',').filter(Boolean);
      const own = `,${target.dataset.handle},`;
      const also = target.dataset.ocksa ? `,${target.dataset.ocksa},` : '';
      things = this.things.filter((thing) => {
        const where = thing.dataset.i || '';
        return where.includes(own) || (!!also && where.includes(also));
      });
    } else if (target?.matches('a[data-plock="lada"]')) {
      const inside = target.closest('.bb-hylla__slot')?.dataset.innehall || '';
      things = this.things.filter((thing) => inside.includes(`,${thing.dataset.handle},`));
    } else if (target?.matches('.bb-rum__sak')) {
      const key = `,${target.dataset.handle},`;
      boxes = this.slots.filter((slot) => (slot.dataset.innehall || '').includes(key)).map((slot) => slot.dataset.box);
      if (!boxes.length) boxes = null;
    }

    for (const slot of this.slots) {
      const match = !!boxes && boxes.includes(slot.dataset.box);
      slot.classList.toggle('is-match', match);
      slot.classList.toggle('is-dim', !!boxes && !match);
    }

    const along = things && things.length ? things : null;
    for (const thing of this.things) {
      const goes = !!along && along.includes(thing);
      thing.classList.toggle('is-med', goes);
      thing.classList.toggle('is-dim', !!along && !goes && thing !== target);
    }
    if (along && !stillness()) along.forEach((thing, index) => jump(thing, index));
  }

  /** A tap chooses `link` (null lets go): it lights up and the card comes. */
  #choose(link) {
    if (link === this.chosen) return;
    this.chosen?.classList.remove('is-vald');
    this.chosen = link;
    link?.classList.add('is-vald');
    this.#show(link);
    this.#card(link);
    // A thing in the room hops as it is chosen; a box opens its lid instead.
    if (link?.matches('.bb-rum__sak') && !stillness()) jump(link, 0);
    if (link) {
      document.addEventListener('pointerup', this.#outside, true);
      document.addEventListener('keydown', this.#escape);
    } else {
      document.removeEventListener('pointerup', this.#outside, true);
      document.removeEventListener('keydown', this.#escape);
    }
  }

  /**
   * A tap anywhere but on a box, a thing or the card lets go. Scrolling does
   * not: a finger that scrolls ends in pointercancel, not pointerup.
   */
  #outside = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('.bb-val')) return;
    const other = target?.closest(CHOOSABLE);
    if (other && this.contains(other)) return;
    this.#choose(null);
  };

  #escape = (event) => {
    if (event.key === 'Escape') this.#choose(null);
  };

  /**
   * The card for what a tap chose: its name, a line about it and Öppna (Visa
   * for a product), which takes you on. It sits under a box, over the name
   * it repeats; under what hangs on the wall; over what stands on the floor.
   */
  #card(link) {
    let card = this.card;
    if (!link) {
      if (card) {
        card.classList.remove('is-uppe');
        card.hidden = true;
      }
      return;
    }
    if (!card) {
      card = this.card = document.createElement('button');
      card.type = 'button';
      card.className = 'bb-val';
      card.hidden = true;
      const text = document.createElement('span');
      text.className = 'bb-val__text';
      const name = document.createElement('span');
      name.className = 'bb-val__namn';
      const info = document.createElement('span');
      info.className = 'bb-val__info';
      const go = document.createElement('span');
      go.className = 'bb-val__knapp';
      text.append(name, info);
      card.append(text, go);
      card.addEventListener('click', () => {
        if (this.chosen) this.#go(this.chosen);
      });
      this.append(card);
    }

    const word = link.matches('.bb-rum__sak') ? 'Visa' : 'Öppna';
    card.querySelector('.bb-val__namn').textContent = link.dataset.title || '';
    card.querySelector('.bb-val__info').textContent = link.dataset.info || '';
    card.querySelector('.bb-val__knapp').textContent = word;
    card.setAttribute('aria-label', `${word} ${link.dataset.title || ''}`.trim());

    card.classList.remove('is-uppe');
    card.hidden = false;
    const art = link.querySelector('.bb-crate, .bb-rum__bild') || link;
    const spot = art.getBoundingClientRect();
    const host = this.getBoundingClientRect();
    const over = link.matches('.bb-rum__sak') && !!link.closest('.bb-rum__golv');
    const width = card.offsetWidth;
    const height = card.offsetHeight;
    const middle = spot.left + spot.width / 2 - host.left;
    const left = Math.max(8, Math.min(middle - width / 2, host.width - width - 8));
    const top = over ? spot.top - host.top - height - 10 : spot.bottom - host.top + 8;
    card.style.left = `${left}px`;
    card.style.top = `${top}px`;
    card.style.setProperty('--pil', `${Math.max(16, Math.min(middle - left, width - 16))}px`);
    card.classList.toggle('is-over', over);
    // Pops out from the arrow on the next frame.
    requestAnimationFrame(() => requestAnimationFrame(() => card.classList.add('is-uppe')));
  }
}

/** The running jump of each thing, so a jump is not started again mid-air. */
const jumping = new WeakMap();

/**
 * A thing in the room shows that it belongs: what hangs on the wall jigs on
 * its hook, the socket cover pulses and the rest hops on the spot. `index`
 * staggers them, one after the other. The drawing inside moves, so a thing
 * that is lifted under the pointer or chosen hops from where it is.
 */
function jump(thing, index) {
  const art = thing.querySelector('.bb-rum__bild > svg, .bb-rum__bild > img');
  if (!(art instanceof Element)) return;
  if (jumping.get(art)?.playState === 'running') return;

  let keyframes;
  let duration;
  if (thing.closest('.bb-rum__topp')) {
    keyframes = [
      { transform: 'none' },
      { transform: 'translateY(-4%) rotate(5deg)', offset: 0.28 },
      { transform: 'rotate(-3deg)', offset: 0.56 },
      { transform: 'rotate(1.2deg)', offset: 0.8 },
      { transform: 'none' },
    ];
    duration = 820;
  } else if (thing.closest('.bb-rum__uttag')) {
    keyframes = [{ transform: 'none' }, { transform: 'scale(1.07)', offset: 0.45 }, { transform: 'none' }];
    duration = 560;
  } else {
    keyframes = [
      { transform: 'none', easing: 'cubic-bezier(.3, .6, .4, 1)' },
      { transform: 'translateY(-11%)', offset: 0.42, easing: 'cubic-bezier(.5, 0, .75, .5)' },
      { transform: 'translateY(0) scale(1.025, .975)', offset: 0.78, easing: 'ease-out' },
      { transform: 'none' },
    ];
    duration = 620;
  }
  jumping.set(art, art.animate(keyframes, { duration, delay: index * 70 }));
}

if (!customElements.get('bb-hylla')) {
  customElements.define('bb-hylla', BbHylla);
}

async function pick(link) {
  const href = link.href;
  // The next page looks for this note: the collection page of a stage
  // (k: steg) or the page of a ready box (k: lada) opens the box it shows.
  try {
    window.sessionStorage.setItem(
      PICKED_KEY,
      JSON.stringify({ h: link.dataset.handle, t: Date.now(), k: link.dataset.plock })
    );
  } catch {
    /* Without storage the next page simply decides on its own. */
  }

  const box = link.querySelector('.bb-crate');
  if (stillness() || !box || document.querySelector('.bb-oppna--plock')) {
    window.location.assign(href);
    return;
  }

  const from = box.getBoundingClientRect();

  // The same layer the collection page starts with, so the box is in the same
  // place on both sides of the page change.
  const layer = document.createElement('div');
  layer.className = 'bb-oppna bb-oppna--plock is-igang';
  layer.setAttribute('aria-hidden', 'true');
  const wall = document.createElement('div');
  wall.className = 'bb-oppna__vagg';
  const holder = document.createElement('div');
  holder.className = 'bb-oppna__lada';
  // The copy shows the pictures the box already has at once: a copy of a
  // lazy picture would wait to be seen first and show empty meanwhile.
  for (const img of box.querySelectorAll('img')) img.loading = 'eager';
  const ghost = /** @type {HTMLElement} */ (box.cloneNode(true));
  ghost.classList.add('bb-crate--stor');
  ghost.style.removeProperty('visibility');
  // It starts on the sharper sticker the box on the next page will want, so
  // that one is likely loaded by the time it is needed.
  const own = box.querySelector('.bb-crate__sticker-bild');
  const sticker = ghost.querySelector('.bb-crate__sticker-bild');
  if (own instanceof HTMLImageElement && sticker instanceof HTMLImageElement && /[?&]width=\d+/.test(own.currentSrc)) {
    sticker.dataset.skarp = own.currentSrc.replace(/([?&]width=)\d+/, '$1480');
  }
  holder.append(ghost);
  const title = document.createElement('p');
  title.className = 'bb-oppna__titel';
  title.textContent = link.dataset.title || '';
  layer.append(wall, holder, title);
  document.body.append(layer);
  sharpen(holder);

  const to = holder.getBoundingClientRect();
  const dx = from.left + from.width / 2 - (to.left + to.width / 2);
  const dy = from.top + from.height / 2 - (to.top + to.height / 2);
  const scale = from.width / to.width;

  link.classList.add('is-plockad');

  const flight = holder.animate(
    [
      { transform: `translate(${dx}px, ${dy}px) scale(${scale})` },
      // Out of its row first: a little up and toward you.
      { transform: `translate(${dx}px, ${dy - from.height * 0.35}px) scale(${scale * 1.12})`, offset: 0.22 },
      { transform: 'none' },
    ],
    { duration: 560, easing: 'cubic-bezier(.3, .8, .3, 1)', fill: 'both' }
  );
  wall.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 300, delay: 80, easing: 'ease', fill: 'both' });
  title.animate([{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'none' }], {
    duration: 260,
    delay: 360,
    easing: 'ease-out',
    fill: 'both',
  });

  await Promise.race([flight.finished.catch(() => {}), wait(760)]);
  window.location.assign(href);
}

/* Back from the next page with the browser's back button: the page comes
   back as it was left, layer and all, so tidy up. */
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  document.querySelectorAll('.bb-oppna--plock, .bb-rosett').forEach((layer) => layer.remove());
  document.querySelectorAll('.is-plockad').forEach((link) => link.classList.remove('is-plockad'));
});

/* ---------- 2. The box opens on the collection page ---------- */

const opening = document.querySelector('.bb-oppna[data-oppna]:not([hidden])');
if (opening instanceof HTMLElement) open(opening);

/**
 * What scrolls the page. On a phone that is the window; on a wide screen the
 * theme scrolls its .page-wrapper instead and keeps html and body still
 * (base.css), so the nearest scrolling ancestor is used when there is one.
 */
function scrollerOf(element) {
  for (let node = element.parentElement; node && node !== document.body; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) return node;
  }
  return window;
}

/** The card art a falling product lands in: the product on its plank, or the theme's photo. */
function landingSpots() {
  const spots = [];
  const items = document.querySelectorAll('.product-grid__item, .resource-list__item');
  for (const item of items) {
    const spot = item.querySelector('.bb-kort__art') || item.querySelector('.card-gallery img');
    if (!(spot instanceof HTMLElement)) continue;
    const rect = spot.getBoundingClientRect();
    if (rect.width < 8 || rect.bottom < 0 || rect.top > window.innerHeight - 8) continue;
    spots.push({ spot, item, rect });
    if (spots.length === 10) break;
  }
  return spots;
}

/** A copy of what is in the card, to fly: the drawing, photo or little box itself. */
function copyOf(spot) {
  if (spot instanceof HTMLImageElement) return spot.cloneNode(true);
  const inside = spot.firstElementChild;
  return inside ? inside.cloneNode(true) : null;
}

/**
 * A big box's sticker starts with the small boxes' picture, which is always
 * loaded already, so it is never empty; the sharper one in data-skarp takes
 * its place once it has loaded and decoded, so the swap does not show.
 */
function sharpen(scope) {
  for (const img of scope.querySelectorAll('img[data-skarp]')) {
    const sharp = new Image();
    sharp.src = img.dataset.skarp || '';
    sharp.decode().then(
      () => {
        // The loaded picture itself takes the place, so there is never a
        // moment of waiting for it again.
        sharp.className = img.className;
        sharp.alt = img.alt;
        if (img.isConnected) img.replaceWith(sharp);
      },
      () => {}
    );
    img.removeAttribute('data-skarp');
  }
}

async function open(layer) {
  layer.classList.add('is-igang');
  sharpen(layer);
  const animations = [];
  const landed = [];
  let skipped = false;

  const end = (animation) => {
    try {
      animation.finish();
    } catch {
      /* An animation without an end is cancelled instead. */
      animation.cancel();
    }
  };
  const skip = () => {
    if (skipped) return;
    skipped = true;
    animations.forEach(end);
  };
  /* Keep every animation, and end at once any that starts after a skip. */
  const track = (animation) => {
    animations.push(animation);
    if (skipped) end(animation);
    return animation;
  };
  layer.addEventListener('pointerdown', skip);
  window.addEventListener('keydown', skip, { once: true });
  window.addEventListener('wheel', skip, { once: true, passive: true });
  window.addEventListener('touchmove', skip, { once: true, passive: true });

  const done = () => {
    for (const { spot, item } of landed) {
      spot.style.removeProperty('visibility');
      item.style.removeProperty('animation');
    }
    layer.remove();
    document.documentElement.classList.remove('bb-oppnar');
    window.removeEventListener('keydown', skip);
    window.removeEventListener('wheel', skip);
    window.removeEventListener('touchmove', skip);
  };

  try {
    // Let the theme's page transition finish first, so the opening is seen.
    if (window.bbOppnaVantar) await Promise.race([window.bbOppnaVantar.catch(() => {}), wait(500)]);

    // Bring the grid up behind the box: its first row a third of the way down.
    const grid = document.querySelector('.product-grid, .resource-list');
    if (grid) {
      scrollerOf(grid).scrollBy({
        top: grid.getBoundingClientRect().top - window.innerHeight * 0.34,
        left: 0,
        behavior: 'instant',
      });
    }

    const spots = landingSpots();
    for (const entry of spots) {
      entry.spot.style.visibility = 'hidden';
      entry.item.style.animation = 'none';
      landed.push(entry);
    }

    const holder = layer.querySelector('.bb-oppna__lada');
    const box = layer.querySelector('.bb-crate');
    const title = layer.querySelector('.bb-oppna__titel');
    const hint = layer.querySelector('.bb-oppna__hoppa');

    // The lid comes off and the first things inside look over the edge.
    await wait(skipped ? 0 : 100);
    box?.style.setProperty('--bb-open', '1');
    await wait(skipped ? 0 : 300);

    const mouth = (layer.querySelector('.bb-crate__mouth') || holder)?.getBoundingClientRect();
    const peek = layer.querySelector('.bb-crate__peek');
    if (peek) track(peek.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 200, fill: 'forwards' }));

    const flights = spots.map(({ spot, rect }, index) => {
      const copy = copyOf(spot);
      if (!copy || !mouth) return Promise.resolve();
      const piece = document.createElement('div');
      piece.className = 'bb-oppna__sak';
      piece.style.left = `${rect.left}px`;
      piece.style.top = `${rect.top}px`;
      piece.style.width = `${rect.width}px`;
      piece.style.height = `${rect.height}px`;
      piece.append(copy);
      layer.append(piece);

      // From the middle of the box's opening, up over the box, then down into the card.
      const startX = mouth.left + mouth.width / 2 - (rect.left + rect.width / 2);
      const startY = mouth.top + mouth.height * 0.7 - (rect.top + rect.height / 2);
      const side = index % 2 ? 1 : -1;
      const peakX = startX * 0.45 + side * (24 + ((index * 37) % 60));
      const peakY = Math.min(startY, 0) - window.innerHeight * (0.12 + (index % 3) * 0.035);
      const startScale = Math.min(0.55, (mouth.width * 0.45) / rect.width);
      const spin = side * (10 + ((index * 23) % 16));
      const distance = Math.hypot(startX, startY);

      const flight = piece.animate(
        [
          { transform: `translate(${startX}px, ${startY}px) scale(${startScale}) rotate(0deg)`, opacity: 0, offset: 0 },
          {
            transform: `translate(${startX}px, ${startY - 24}px) scale(${startScale * 1.1}) rotate(0deg)`,
            opacity: 1,
            offset: 0.08,
            easing: 'cubic-bezier(.2, .7, .4, 1)',
          },
          {
            transform: `translate(${peakX}px, ${peakY}px) scale(.82) rotate(${spin}deg)`,
            opacity: 1,
            offset: 0.42,
            easing: 'cubic-bezier(.55, 0, .9, .45)',
          },
          { transform: 'translate(0, 0) scale(1) rotate(0deg)', opacity: 1, offset: 0.9, easing: 'ease-out' },
          { transform: 'translate(0, -4%) scale(1.03, .97)', opacity: 1, offset: 0.95 },
          { transform: 'none', opacity: 1, offset: 1 },
        ],
        { duration: 780 + Math.min(320, distance * 0.36), delay: index * 70, fill: 'both' }
      );
      track(flight);

      return flight.finished
        .catch(() => {})
        .then(() => {
          spot.style.removeProperty('visibility');
          piece.remove();
          if (!skipped) spot.animate([{ scale: '1.05 0.95' }, { scale: '1' }], { duration: 240, easing: 'ease-out' });
        });
    });

    // The wall colour lets go so the products land in the shop, and the box
    // and its name step aside once everything is out.
    const wall = layer.querySelector('.bb-oppna__vagg');
    if (wall) {
      track(wall.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 500, delay: 180, easing: 'ease', fill: 'forwards' }));
    }
    if (title) track(title.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 220, fill: 'forwards' }));
    if (hint) track(hint.animate([{ opacity: 0.55 }, { opacity: 0 }], { duration: 200, fill: 'forwards' }));
    if (holder) {
      track(
        holder.animate([{ transform: 'none', opacity: 1 }, { transform: 'translateY(-36px) scale(.86)', opacity: 0 }], {
          duration: 340,
          delay: 180 + Math.max(0, spots.length - 1) * 70,
          easing: 'ease-in',
          fill: 'forwards',
        })
      );
    }

    // Clicks pass through to the shop once the products are falling.
    layer.style.pointerEvents = 'none';
    window.addEventListener('pointerdown', skip, { once: true });

    await Promise.all([...flights, ...animations.map((animation) => animation.finished.catch(() => {}))]);
  } finally {
    done();
  }
}

/* ---------- 3. The present on the home page ---------- */

const present = document.querySelector('.bb-rosett:not([hidden])');
if (present instanceof HTMLElement) unwrap(present);

/**
 * The boxes on the big shelf that are in view, in order: each with the
 * plank it stands on, the line of that plank it is on (a plank wraps on a
 * phone), and what fades in once it has landed (its name, or its age and
 * price).
 */
function shelfBoxes() {
  const boxes = [];
  const rows = [...document.querySelectorAll('.bb-hylla--stor .bb-hylla__rad')];
  for (const place of document.querySelectorAll('.bb-hylla--stor .bb-hylla__plock')) {
    const crate = place.querySelector('.bb-crate');
    if (!(crate instanceof HTMLElement)) continue;
    const rect = crate.getBoundingClientRect();
    if (rect.width < 8 || rect.bottom < 0 || rect.top > window.innerHeight) continue;
    const row = place.closest('.bb-hylla__rad');
    boxes.push({
      crate,
      rect,
      row: row ? row.getBoundingClientRect() : rect,
      line: `${rows.indexOf(row)}:${Math.round(rect.top / 8)}`,
      slot: place.closest('li') || place,
      labels: [...place.querySelectorAll('.bb-hylla__etikett, .bb-hylla__meta, .bb-hylla__snart')],
    });
  }
  return boxes;
}

/**
 * The things in the room (the shelf as the cover, sections/bb-hylla.liquid)
 * that are in view, each with how it arrives: what hangs on the wall swings
 * ('sving'), the towel comes up out of its basket ('upp'), the socket cover
 * pops onto the wall ('pop') and the rest drops onto the floor ('ner').
 */
function roomThings() {
  const things = [];
  for (const thing of document.querySelectorAll('.bb-rum .bb-rum__sak')) {
    const art = thing.querySelector('.bb-rum__bild');
    if (!(art instanceof HTMLElement)) continue;
    const rect = art.getBoundingClientRect();
    if (rect.width < 8 || rect.bottom < 0 || rect.top > window.innerHeight) continue;
    let how = 'ner';
    if (thing.closest('.bb-rum__topp')) how = 'sving';
    else if (thing.classList.contains('bb-rum__sak--i-korgen')) how = 'upp';
    else if (thing.closest('.bb-rum__uttag')) how = 'pop';
    things.push({ art, how });
  }
  return things;
}

/**
 * How a thing in the room arrives (see roomThings), as keyframes and
 * options for animate(). `side` is 1 or -1, so neighbours swing opposite
 * ways.
 */
function arrival(how, side) {
  switch (how) {
    case 'sving':
      return {
        keyframes: [
          { transform: `rotate(${side * 9}deg)` },
          { transform: `rotate(${side * -5}deg)`, offset: 0.32 },
          { transform: `rotate(${side * 2.5}deg)`, offset: 0.6 },
          { transform: `rotate(${side * -1}deg)`, offset: 0.82 },
          { transform: 'none' },
        ],
        options: { duration: 1500, easing: 'ease-in-out' },
      };
    // Down inside the basket, and not shown until it starts to come up, so
    // it does not wait in the air while its basket is still falling.
    case 'upp':
      return {
        keyframes: [
          { transform: 'translateY(80%)', opacity: 0, offset: 0, easing: 'cubic-bezier(.3, .7, .4, 1)' },
          { opacity: 1, offset: 0.01 },
          { transform: 'translateY(-7%)', offset: 0.6, easing: 'ease-in-out' },
          { transform: 'translateY(1%)', offset: 0.84, easing: 'ease-in-out' },
          { transform: 'none', opacity: 1, offset: 1 },
        ],
        options: { duration: 700 },
      };
    case 'pop':
      return {
        keyframes: [
          { transform: 'scale(.4)', opacity: 0, easing: 'cubic-bezier(.3, .7, .4, 1)' },
          { transform: 'scale(1.04)', opacity: 1, offset: 0.65 },
          { transform: 'none', opacity: 1 },
        ],
        options: { duration: 520 },
      };
    default:
      return {
        keyframes: [
          { transform: 'translateY(-40%)', opacity: 0, offset: 0, easing: 'cubic-bezier(.4, 0, .7, .6)' },
          { opacity: 1, offset: 0.3 },
          { transform: 'translateY(0) scale(1.03, .97)', offset: 0.68, easing: 'ease-out' },
          { transform: 'translateY(-1.5%) scale(.995, 1.005)', offset: 0.85, easing: 'ease-in-out' },
          { transform: 'none', opacity: 1, offset: 1 },
        ],
        options: { duration: 720 },
      };
  }
}

/**
 * Where the boxes of one line lie in their heap, for `count` boxes: a
 * bottom layer of two or three side by side and smaller layers on top.
 * `level` counts layers from the bottom, `across` is the place in the layer
 * from the middle, in box widths. Listed top first, because the box that
 * goes to the first place on the plank is taken off the top.
 */
function heapSpots(count) {
  const layers = count >= 5 ? [3, 2, 2, 1, 1, 1] : [2, 2, 1, 1];
  const spots = [];
  let left = count;
  for (const [level, room] of layers.entries()) {
    const size = Math.min(room, left);
    for (let i = 0; i < size; i++) spots.push({ level, across: i - (size - 1) / 2 });
    left -= size;
    if (left <= 0) break;
  }
  return spots.reverse();
}

async function unwrap(layer) {
  let shownAt = window.bbRosettT0 ?? performance.now();
  // The CSS lets the present go by itself after five seconds; this late it
  // is already on its way out.
  if (performance.now() - shownAt > 4400) {
    layer.remove();
    return;
  }
  layer.classList.add('is-igang');

  // Opened in a background tab: nothing moves there, so the present waits
  // until the tab is looked at.
  if (document.visibilityState === 'hidden') {
    await new Promise((resolve) => {
      const seen = () => {
        if (document.visibilityState === 'hidden') return;
        document.removeEventListener('visibilitychange', seen);
        resolve();
      };
      document.addEventListener('visibilitychange', seen);
    });
    shownAt = performance.now();
  }

  const animations = [];
  const timers = new Set();
  let skipped = false;
  let opening = false;
  let begin = () => {};
  const tapped = new Promise((resolve) => {
    begin = resolve;
  });

  const end = (animation) => {
    try {
      animation.finish();
    } catch {
      animation.cancel();
    }
  };
  const skip = () => {
    if (skipped) return;
    skipped = true;
    begin();
    for (const timer of timers) window.clearTimeout(timer);
    timers.clear();
    animations.forEach(end);
  };
  const track = (animation) => {
    animations.push(animation);
    if (skipped) end(animation);
    return animation;
  };
  /* A step a little later, unless everything has been finished already. */
  const later = (ms, step) => {
    if (skipped) return;
    const timer = window.setTimeout(() => {
      timers.delete(timer);
      step();
    }, ms);
    timers.add(timer);
  };
  /* An animation that waits at its first frame until it is played. */
  const ready = (element, keyframes, options) => {
    const animation = track(element.animate(keyframes, options));
    if (!skipped) animation.pause();
    return animation;
  };

  // Before it opens, a tap opens it; once it is opening, a tap finishes it.
  layer.addEventListener('pointerdown', () => (opening ? skip() : begin()));
  window.addEventListener('keydown', skip, { once: true });
  window.addEventListener('wheel', skip, { once: true, passive: true });
  window.addEventListener('touchmove', skip, { once: true, passive: true });

  // Under the paper the boxes already wait in a heap in the middle of their
  // plank, their names not yet there.
  const found = shelfBoxes();
  const lines = new Map();
  for (const box of found) lines.set(box.line, [...(lines.get(box.line) || []), box]);
  const heaps = new Map([...lines].map(([line, boxes]) => [line, heapSpots(boxes.length)]));
  const hops = found.map((box, index) => {
    const { crate, rect, row, slot, labels } = box;
    const spot = heaps.get(box.line)[lines.get(box.line).indexOf(box)];
    const side = index % 2 ? 1 : -1;
    const fromX = row.left + row.width / 2 + spot.across * rect.width * 0.82 - (rect.left + rect.width / 2) + side * rect.width * 0.04;
    const fromY = rect.height * 0.04 - spot.level * rect.height * 0.5;
    const tilt = side * (2 + ((index * 5) % 5));
    const lift = rect.height * (0.3 + (index % 3) * 0.06);
    crate.style.zIndex = String(100 - index);
    crate.style.transformOrigin = '50% 100%';
    const hop = ready(
      crate,
      [
        {
          transform: `translate(${fromX}px, ${fromY}px) rotate(${tilt}deg) scale(.86)`,
          offset: 0,
          easing: 'cubic-bezier(.25, .75, .45, 1)',
        },
        {
          transform: `translate(${fromX * 0.42}px, ${Math.min(fromY, 0) - lift}px) rotate(${tilt * -0.35}deg) scale(1.04)`,
          offset: 0.5,
          easing: 'cubic-bezier(.55, 0, .9, .5)',
        },
        { transform: 'translate(0, 0) scale(1.035, .965)', offset: 0.82, easing: 'ease-out' },
        { transform: 'translate(0, 0) scale(.995, 1.005)', offset: 0.92, easing: 'ease-in-out' },
        { transform: 'none', offset: 1 },
      ],
      { duration: 680 }
    );
    const fades = labels.map((label) => ready(label, [{ opacity: 0 }, { opacity: 1 }], { duration: 280, easing: 'ease-out' }));
    return { crate, slot, hop, fades };
  });
  const heading = document.querySelector('.bb-hylla--stor .bb-hylla__huvud');
  const headingIn = heading
    ? ready(heading, [{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'none' }], {
        duration: 420,
        easing: 'ease-out',
      })
    : null;

  // In the room the headline comes up with them, and the things in the room
  // arrive too (roomThings).
  const roomText = document.querySelector('.bb-rum__text');
  const roomTextIn = roomText
    ? ready(roomText, [{ opacity: 0, transform: 'translateY(14px)' }, { opacity: 1, transform: 'none' }], {
        duration: 520,
        easing: 'ease-out',
      })
    : null;
  const arrivals = roomThings().map(({ art, how }, index) => {
    const { keyframes, options } = arrival(how, index % 2 ? -1 : 1);
    return { how, arrival: ready(art, keyframes, options) };
  });

  const done = () => {
    for (const { crate, slot } of hops) {
      crate.style.removeProperty('z-index');
      crate.style.removeProperty('transform-origin');
      slot.style.removeProperty('--bb-open');
    }
    layer.remove();
    window.removeEventListener('keydown', skip);
    window.removeEventListener('wheel', skip);
    window.removeEventListener('touchmove', skip);
    window.removeEventListener('pointerdown', skip);
  };

  try {
    // A moment to see the present, unless someone taps it first.
    await Promise.race([wait(Math.max(0, 650 - (performance.now() - shownAt))), tapped]);
    opening = true;

    const part = (selector) => layer.querySelector(selector);
    const away = (selector, to, options) => {
      const element = part(selector);
      if (element) {
        track(element.animate([{ transform: 'none', opacity: 1 }, { transform: to, opacity: 0 }], { fill: 'forwards', ...options }));
      }
    };

    // 1. A tug at the bow.
    const knot = part('.bb-rosett__knut');
    if (knot) {
      track(
        knot.animate(
          [
            { transform: 'rotate(0deg) scale(1)' },
            { transform: 'rotate(-6deg) scale(1.04)', offset: 0.3 },
            { transform: 'rotate(5deg) scale(1.02)', offset: 0.62 },
            { transform: 'rotate(0deg) scale(1)' },
          ],
          { duration: 380, easing: 'ease-in-out' }
        )
      );
    }

    // 2. It comes undone: the loops fold into the knot, the tails and the tag drop.
    const fold = 'cubic-bezier(.5, 0, .75, 0)';
    const fall = 'cubic-bezier(.55, 0, 1, .45)';
    away('.bb-rosett__trad', 'none', { duration: 140, delay: 260 });
    away('.bb-rosett__lapp', 'translate(22%, 75vh) rotate(46deg)', { duration: 760, delay: 260, easing: fall });
    away('.bb-rosett__ogla--v', 'rotate(-28deg) scale(.12)', { duration: 420, delay: 300, easing: fold });
    away('.bb-rosett__ogla--h', 'rotate(28deg) scale(.12)', { duration: 420, delay: 330, easing: fold });
    away('.bb-rosett__svans--v', 'translate(-14%, 34%) rotate(16deg)', { duration: 480, delay: 360, easing: fall });
    away('.bb-rosett__svans--h', 'translate(14%, 34%) rotate(-16deg)', { duration: 480, delay: 380, easing: fall });
    away('.bb-rosett__mitt', 'scale(0)', { duration: 240, delay: 600, easing: 'cubic-bezier(.6, -0.5, .7, 0)' });

    // 3. The ribbons slip off towards the edges.
    const slip = { duration: 420, delay: 540, easing: 'cubic-bezier(.7, 0, .3, 1)', fill: 'forwards' };
    for (const band of layer.querySelectorAll('.bb-rosett__band')) {
      track(band.animate([{ transform: 'scaleX(1)' }, { transform: 'scaleX(0)' }], slip));
    }
    for (const string of layer.querySelectorAll('.bb-rosett__snore')) {
      track(string.animate([{ transform: 'scaleY(1)' }, { transform: 'scaleY(0)' }], slip));
    }

    // 4. The paper opens to both sides, and from here a tap reaches the shop.
    layer.querySelectorAll('.bb-rosett__papper').forEach((paper, index) => {
      const side = index === 0 ? -1 : 1;
      track(
        paper.animate([{ transform: 'none' }, { transform: `translateX(${side * 104}%) rotate(${side * 2}deg)` }], {
          duration: 760,
          delay: 760,
          easing: 'cubic-bezier(.65, 0, .25, 1)',
          fill: 'forwards',
        })
      );
    });
    later(760, () => {
      layer.style.pointerEvents = 'none';
      window.addEventListener('pointerdown', skip, { once: true });
    });

    // 5. The boxes hop to their places, one after the other, and each lifts
    //    its lid as it lands. In the room what hangs on the wall swings as
    //    the paper goes by, and what stands on the floor lands while the
    //    boxes do; last the towel peeks up out of its basket.
    if (roomTextIn) later(760, () => roomTextIn.play());
    if (headingIn) later(820, () => headingIn.play());
    const swinging = arrivals.filter(({ how }) => how === 'sving');
    const landing = arrivals.filter(({ how }) => how === 'ner' || how === 'pop');
    swinging.forEach(({ arrival }, index) => later(800 + index * 80, () => arrival.play()));
    landing.forEach(({ arrival }, index) => later(1150 + index * 100, () => arrival.play()));
    arrivals
      .filter(({ how }) => how === 'upp')
      .forEach(({ arrival }) => later(1150 + landing.length * 100 + 150, () => arrival.play()));
    hops.forEach(({ slot, hop, fades }, index) => {
      later(860 + index * 85, () => hop.play());
      hop.finished
        .catch(() => {})
        .then(() => {
          // After a skip everything is already in place.
          if (skipped) return;
          fades.forEach((fade) => fade.play());
          slot.style.setProperty('--bb-open', '1');
          later(300, () => slot.style.removeProperty('--bb-open'));
        });
    });

    await Promise.all(animations.map((animation) => animation.finished.catch(() => {})));
    // The last lid closes again.
    if (!skipped) await wait(340);
  } finally {
    done();
  }
}
